import React, { useEffect, useMemo, useState } from 'react';
import { inject } from 'mobx-react';
import Link from 'core/components/Link';
import { GROUP_ID_PREFIX } from '@kentik/ui-shared/agents/constants';
import { KentikAgentOptionRenderer } from 'app/views/setup/tasks/nms/optionRenderers';

import { getHashForObject } from 'app/stores/query/urlHash';

const hashCache = new Map();

function getOrCreateHash(cacheKey, compute) {
  if (hashCache.has(cacheKey)) {
    return Promise.resolve(hashCache.get(cacheKey));
  }
  const promise = Promise.resolve()
    .then(compute)
    .then((hash) => {
      hashCache.set(cacheKey, hash);
      return hash;
    });
  hashCache.set(cacheKey, promise);
  return promise;
}

function getHashForAgent(agentName) {
  const key = `agent:${agentName}`;
  return getOrCreateHash(key, () => getHashForObject({ searchTerm: agentName }));
}

function getHashForCluster(clusterName) {
  const key = `cluster:${clusterName}`;
  return getOrCreateHash(key, () => getHashForObject({ hashedFilters: { cluster_name: [clusterName] } }));
}

const KentikAgentLink = ({ $app, $kentikAgents, $auth, id, name, minimal, ...rest }) => {
  const [hash, setHash] = useState(null);
  const [agentName, setAgentName] = useState(null);

  const isGroup = useMemo(() => Boolean(id?.startsWith(GROUP_ID_PREFIX)), [id]);

  // Resolve agent name when inputs change
  useEffect(() => {
    if (name) {
      setAgentName(name);
      return;
    }

    if (id && !isGroup) {
      $kentikAgents.lookupAgents([id]).then((agents) => {
        const resolved = agents?.[0]?.name;
        if (resolved) {
          setAgentName(resolved);
        }
      });
    }
  }, [name, id, isGroup, $kentikAgents]);

  // Compute hash when relevant inputs change
  useEffect(() => {
    // Clear hash when dependencies change to avoid stale links
    setHash(null);

    if ($app.isSubtenant) {
      return;
    }

    if (isGroup && name) {
      getHashForCluster(name).then((_hash) => {
        setHash(_hash);
      });
    } else if (!isGroup && agentName) {
      getHashForAgent(agentName).then((_hash) => {
        setHash(_hash);
      });
    }
  }, [$app.isSubtenant, isGroup, name, agentName]);

  if (isGroup) {
    const groupOption = $kentikAgents.getClusterGroupOption(id);
    return (
      <Link to={`/v4/settings/universal-agents${hash ? `/?q=${hash}` : ''}`}>
        <KentikAgentOptionRenderer {...groupOption} label={groupOption?.label ?? name ?? id} minimal={minimal} />
      </Link>
    );
  }

  if ($app.isSubtenant) {
    return agentName || id;
  }

  const isSudo = $auth.isSudoer;
  const shouldAddIdToLink = isSudo && !isGroup && id && agentName;

  return (
    <Link to={`/v4/settings/universal-agents${hash ? `/?q=${hash}` : ''}`} {...rest}>
      {agentName || id} {shouldAddIdToLink && `(${id})`}
    </Link>
  );
};

export default inject('$app', '$kentikAgents', '$auth')(KentikAgentLink);
